import { v4 as uuidv4 } from "uuid";
/** Threshold for merging segments from the same speaker (in ms) */
export const MERGE_THRESHOLD_MS = 30000;
/** Threshold for merging segments from the same speaker (in ticks, 100ns units) */
export const MERGE_THRESHOLD_TICKS = MERGE_THRESHOLD_MS * 10000;
/**
 * Formats a time offset in seconds to a human-readable HH:MM:SS format
 * @param {number} totalSeconds
 */
export const formatOffset = (totalSeconds) => {
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    const parts = [];
    if (hours > 0) {
        parts.push(hours.toString().padStart(2, "0"));
    }
    parts.push(minutes.toString().padStart(2, "0"));
    parts.push(seconds.toString().padStart(2, "0"));
    return parts.join(":");
};
/**
 * Formats a time offset in ticks (100ns units) to a human-readable HH:MM:SS format
 * @param {number} ticks - The time offset in ticks
 * @returns {string} The formatted time string
 */
export const formatOffsetTicks = (ticks) => {
    const totalSeconds = Math.floor(ticks / 10000000);
    return formatOffset(totalSeconds);
};
/**
 * Formats a time offset in milliseconds (ms) to a human-readable HH:MM:SS format
 * @param {number} ms - The time offset in milliseconds
 * @returns {string} The formatted time string
 */
export const formatOffsetMs = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    return formatOffset(totalSeconds);
};
const SPEAKER_ID_PREFIX = "Speaker-";
const SPEAKER_DISPLAY_PREFIX = "Guest ";
const toSpeakerDisplayName = (speakerId) => speakerId.startsWith(SPEAKER_ID_PREFIX)
    ? speakerId.replace(SPEAKER_ID_PREFIX, SPEAKER_DISPLAY_PREFIX)
    : speakerId;
export const normaliseTranscriptSpeakers = (segments, speakers, mergeThresholdMs) => {
    const merged = [];
    segments.forEach((segment) => {
        const renamed = {
            ...segment,
            speakerId: toSpeakerDisplayName(segment.speakerId),
        };
        const previous = merged[merged.length - 1];
        const continuesPrevious = previous !== undefined &&
            previous.speakerId === renamed.speakerId &&
            renamed.offset - previous.offset < mergeThresholdMs;
        if (continuesPrevious) {
            previous.text += ` ${renamed.text}`;
        }
        else {
            merged.push(renamed);
        }
    });
    const renamedSpeakers = {};
    Object.keys(speakers).forEach((speakerId) => {
        const displayName = toSpeakerDisplayName(speakerId);
        renamedSpeakers[displayName] = displayName;
    });
    return { segments: merged, speakers: renamedSpeakers };
};
/**
 * Generates the final formatted text from segments and speaker names
 */
export const getFormattedTranscription = (segments, speakers) => {
    const hasSpeakers = Object.keys(speakers).length > 0;
    if (!hasSpeakers) {
        return segments
            .filter((s) => !s.isSeparator)
            .map((s) => s.text)
            .join(" ");
    }
    let formatted = "";
    segments
        .filter((s) => !s.isSeparator)
        .forEach((segment) => {
        const speakerName = speakers[segment.speakerId] || segment.speakerId;
        const timeStr = segment.isRealTimePreview
            ? formatOffsetTicks(segment.offset)
            : formatOffsetMs(segment.offset);
        if (!formatted) {
            formatted = `${speakerName} (${timeStr}):\n${segment.text}`;
        }
        else {
            formatted += `\n\n${speakerName} (${timeStr}):\n${segment.text}`;
        }
    });
    return formatted;
};
export const parseTranscriptionPhrases = (phrases, speakerPrefix = "Speaker") => {
    const segments = [];
    const speakers = {};
    phrases.forEach((phrase) => {
        const speakerId = phrase.speaker !== undefined ? `${speakerPrefix}-${phrase.speaker}` : "Me";
        segments.push({
            id: uuidv4(),
            speakerId,
            text: phrase.text,
            offset: phrase.offsetMilliseconds,
        });
        speakers[speakerId] = speakerId;
    });
    return { segments, speakers };
};
export const formatTranscriptionPhrases = (phrases, mergeThresholdMs = MERGE_THRESHOLD_MS) => {
    const parsed = parseTranscriptionPhrases(phrases);
    const { segments, speakers } = normaliseTranscriptSpeakers(parsed.segments, parsed.speakers, mergeThresholdMs);
    return getFormattedTranscription(segments, speakers);
};
//# sourceMappingURL=transcription.js.map